import {
  TrendingUp,
  Rocket,
  Zap,
  MessageCircle,
  Sparkles,
  BarChart3,
  Quote,
  Eye,
  Linkedin,
  Twitter,
  ArrowRight,
  Search,
  FileText,
  CheckCircle2,
  Activity,
} from "lucide-react";

function Row({ children, glow = false }: { children: React.ReactNode; glow?: boolean }) {
  return (
    <div
      className={`flex items-center justify-between gap-4 rounded-xl px-4 py-3 border border-white/10 ${
        glow ? "bg-gradient-to-r from-pink-soft/15 to-violet/15" : "bg-white/[0.04]"
      }`}
    >
      {children}
    </div>
  );
}

/* ────────────── 1. Live Market Signals ────────────── */
export function LiveMarketSignals() {
  const signals = [
    {
      icon: TrendingUp,
      label: "Search demand rising",
      meta: "“ai workflow automation” +43%",
      glow: true,
    },
    { icon: Rocket, label: "Competitor launch detected", meta: "Linear ships AI memory" },
    {
      icon: Linkedin,
      label: "LinkedIn topic gaining traction",
      meta: "“agentic GTM”  ·  +112 posts/wk",
    },
    { icon: Sparkles, label: "New narrative forming", meta: "“autonomous marketing” cluster" },
    {
      icon: Zap,
      label: "Campaign opportunity found",
      meta: "Generate GEO + LinkedIn series",
      glow: true,
    },
  ];
  return (
    <div className="glass-card p-6 md:p-8">
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-pink-soft mb-1">
            Market Signals
          </p>
          <h3 className="text-lg font-semibold text-white">Live across your category</h3>
        </div>
        <span className="glass-pill px-2.5 py-1 text-[10px] uppercase tracking-wider text-white/80 inline-flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-pink-soft animate-pulse" /> Live
        </span>
      </div>
      <div className="space-y-2.5">
        {signals.map((s) => (
          <Row key={s.label} glow={s.glow}>
            <div className="flex items-center gap-3 min-w-0">
              <span className="grid place-items-center size-8 rounded-lg bg-gradient-to-br from-pink-soft/30 to-violet/30 border border-white/10">
                <s.icon className="size-4 text-white" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{s.label}</p>
                <p className="text-xs text-white/55 truncate">{s.meta}</p>
              </div>
            </div>
            <ArrowRight className="size-4 text-white/40 shrink-0" />
          </Row>
        ))}
      </div>
    </div>
  );
}

/* ────────────── 2. AI Visibility Map ────────────── */
export function AIVisibilityMap() {
  const rows = [
    { llm: "ChatGPT", mention: 62, citation: 28, comp: 71, action: "Add 4 GEO articles" },
    { llm: "Perplexity", mention: 48, citation: 41, comp: 55, action: "Refresh /vs pages" },
    { llm: "Gemini", mention: 31, citation: 12, comp: 67, action: "Build authority hub" },
    { llm: "Claude", mention: 44, citation: 22, comp: 49, action: "Publish FAQ cluster" },
  ];
  return (
    <div className="glass-card p-6 md:p-8">
      <div className="flex items-start justify-between mb-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-pink-soft mb-1">
            AI Visibility
          </p>
          <h3 className="text-lg font-semibold text-white">Share of AI answers</h3>
          <p className="mt-1 text-xs text-white/50">Sample report — illustrative example data</p>
        </div>
        <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full glass-pill px-2.5 py-1 text-[10px] uppercase tracking-wider text-white/70">
          <Eye className="size-3.5 text-white/60" />
          Sample
        </span>
      </div>
      <div className="grid grid-cols-12 text-[10px] uppercase tracking-wider text-white/45 px-3 mb-2">
        <div className="col-span-3">LLM</div>
        <div className="col-span-2">Mention</div>
        <div className="col-span-2">Citation</div>
        <div className="col-span-2">Comp.</div>
        <div className="col-span-3">Action</div>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div
            key={r.llm}
            className="grid grid-cols-12 items-center gap-2 rounded-xl px-3 py-3 bg-white/[0.04] border border-white/10"
          >
            <div className="col-span-3 text-sm font-medium text-white">{r.llm}</div>
            <div className="col-span-2 text-sm text-white/85">{r.mention}%</div>
            <div className="col-span-2 text-sm text-white/85">{r.citation}%</div>
            <div className="col-span-2">
              <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-pink-soft to-violet"
                  style={{ width: `${r.comp}%` }}
                />
              </div>
            </div>
            <div className="col-span-3 text-xs text-white/70 truncate">{r.action}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────── 3. Narrative Performance ────────────── */
export function NarrativePerformance() {
  const channels = [
    { icon: Linkedin, name: "LinkedIn", reach: "284k", eng: "5.2%", trend: "+38%" },
    { icon: Twitter, name: "X", reach: "192k", eng: "3.1%", trend: "+24%" },
  ];
  const topics = [
    { label: "Agentic GTM", score: 94 },
    { label: "AI visibility", score: 81 },
    { label: "Autopilot ops", score: 68 },
  ];
  const next = [
    "Founder POV: agentic vs assistive AI",
    "Thread: 5 GEO myths killing visibility",
    "Carousel: signal → strategy → post",
  ];
  return (
    <div className="glass-card p-6 md:p-8">
      <p className="text-[11px] uppercase tracking-[0.22em] text-pink-soft mb-1">
        Narrative Performance
      </p>
      <h3 className="text-lg font-semibold text-white mb-5">Cross-channel signal</h3>
      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        {channels.map((c) => (
          <div key={c.name} className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-white">
                <c.icon className="size-4" />
                <span className="text-sm font-medium">{c.name}</span>
              </div>
              <span className="text-[11px] text-pink-soft">{c.trend}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs text-white/60">
              <div>
                <div className="text-white text-base font-semibold">{c.reach}</div>reach 30d
              </div>
              <div>
                <div className="text-white text-base font-semibold">{c.eng}</div>engagement
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
          <p className="text-[11px] uppercase tracking-wider text-white/50 mb-3">Top topics</p>
          <div className="space-y-2">
            {topics.map((t) => (
              <div key={t.label}>
                <div className="flex justify-between text-xs text-white/80 mb-1">
                  <span>{t.label}</span>
                  <span>{t.score}</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-pink-soft to-violet"
                    style={{ width: `${t.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-white/10 bg-gradient-to-br from-pink-soft/10 to-violet/10 p-4">
          <p className="text-[11px] uppercase tracking-wider text-pink-soft mb-3">
            Recommended next posts
          </p>
          <ul className="space-y-2 text-xs text-white/85">
            {next.map((n) => (
              <li key={n} className="flex gap-2">
                <Sparkles className="size-3 mt-0.5 text-pink-soft shrink-0" />
                {n}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ────────────── 4. Prompt Cluster Flow (GEO) ────────────── */
export function PromptClusterFlow() {
  const stages = [
    { icon: MessageCircle, title: "Customer prompts", body: '"best ai marketing platform"' },
    { icon: FileText, title: "Content brief", body: "Auto-built from gap analysis" },
    { icon: Search, title: "GEO article", body: "Optimised for citation & rank" },
    { icon: Quote, title: "AI citation", body: "Cited in ChatGPT + Perplexity" },
  ];
  return (
    <div className="glass-card p-6 md:p-8">
      <p className="text-[11px] uppercase tracking-[0.22em] text-pink-soft mb-1">
        Prompt Cluster → Content
      </p>
      <h3 className="text-lg font-semibold text-white mb-6">Search demand turned into citations</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 relative">
        {stages.map((s, i) => (
          <div key={s.title} className="relative">
            <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 h-full">
              <span className="grid place-items-center size-9 rounded-lg bg-gradient-to-br from-pink-soft/30 to-violet/30 border border-white/10 mb-3">
                <s.icon className="size-4 text-white" />
              </span>
              <p className="text-sm font-medium text-white mb-1">{s.title}</p>
              <p className="text-xs text-white/60">{s.body}</p>
            </div>
            {i < stages.length - 1 && (
              <div
                aria-hidden
                className="hidden lg:block absolute top-1/2 -right-2 w-4 h-px flow-line"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────── 5. Signal → Post (LinkedIn) ────────────── */
export function SignalToPost() {
  const stages = [
    { icon: Activity, title: "Signal detected", body: "Spike on “AI GTM ops”" },
    { icon: Sparkles, title: "Suggested angle", body: "Founder POV + framework" },
    { icon: Linkedin, title: "Draft LinkedIn post", body: "Hook · body · CTA" },
    { icon: CheckCircle2, title: "Approval", body: "1-click publish" },
    { icon: BarChart3, title: "Performance feedback", body: "Reach 18.4k · 6.1% eng" },
  ];
  return (
    <div className="glass-card p-6 md:p-8">
      <p className="text-[11px] uppercase tracking-[0.22em] text-pink-soft mb-1">
        From Signal to Post
      </p>
      <h3 className="text-lg font-semibold text-white mb-6">A live LinkedIn loop</h3>
      <div className="space-y-2.5">
        {stages.map((s, i) => (
          <div key={s.title} className="relative">
            <Row glow={i === stages.length - 1}>
              <div className="flex items-center gap-3">
                <span className="grid place-items-center size-8 rounded-lg bg-gradient-to-br from-pink-soft/30 to-violet/30 border border-white/10">
                  <s.icon className="size-4 text-white" />
                </span>
                <div>
                  <p className="text-sm font-medium text-white">{s.title}</p>
                  <p className="text-xs text-white/55">{s.body}</p>
                </div>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-white/40">
                step {i + 1}
              </span>
            </Row>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────── 6. Narrative Response Engine (X) ────────────── */
export function NarrativeResponseEngine() {
  const stages = [
    { icon: TrendingUp, title: "Trending conversation", body: "“AI search killed SEO”" },
    { icon: Twitter, title: "Short post", body: "Direct, contrarian take" },
    { icon: MessageCircle, title: "Thread", body: "5-tweet breakdown w/ data" },
    { icon: Sparkles, title: "Founder commentary", body: "Personalised tone" },
    { icon: BarChart3, title: "Engagement feedback", body: "12k impressions · 184 RTs" },
  ];
  return (
    <div className="glass-card p-6 md:p-8">
      <p className="text-[11px] uppercase tracking-[0.22em] text-pink-soft mb-1">
        Narrative Response Engine
      </p>
      <h3 className="text-lg font-semibold text-white mb-6">From trend to thread, automatically</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {stages.map((s, i) => (
          <div key={s.title} className="relative">
            <div
              className={`rounded-xl border border-white/10 p-4 h-full ${i === stages.length - 1 ? "bg-gradient-to-br from-pink-soft/15 to-violet/15" : "bg-white/[0.04]"}`}
            >
              <span className="grid place-items-center size-9 rounded-lg bg-gradient-to-br from-pink-soft/30 to-violet/30 border border-white/10 mb-3">
                <s.icon className="size-4 text-white" />
              </span>
              <p className="text-sm font-medium text-white mb-1">{s.title}</p>
              <p className="text-xs text-white/60">{s.body}</p>
            </div>
            {i < stages.length - 1 && (
              <div
                aria-hidden
                className="hidden lg:block absolute top-1/2 -right-2 w-4 h-px flow-line"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export const productModules: Record<string, () => React.JSX.Element> = {
  "market-intelligence": LiveMarketSignals,
  "ai-visibility-analytics": AIVisibilityMap,
  "cross-channel-social-analytics": NarrativePerformance,
  "geo-aeo-seo-autopilot": PromptClusterFlow,
  "linkedin-autopilot": SignalToPost,
  "x-autopilot": NarrativeResponseEngine,
};
